import { ALL_THEMES } from 'lib/context/ThemeContext';
import { tv } from 'tailwind-variants';

const themeVariants = ALL_THEMES.reduce(
  (acc, curr) => ((acc[curr] = {}), acc),
  {} as Record<string, object>
);

export const tailwindVariants = tv({
  slots: {
    background: ['overflow-hidden', 'relative'],
    base: [
      'px-themes-general-surface-spacing-small-padding-x',
      'py-themes-general-surface-spacing-small-padding-y',
      'md:px-themes-general-surface-spacing-large-padding-x',
      'md:py-themes-general-surface-spacing-large-padding-y',
    ],
    buttonClasses: [
      'border-[3px]',
      'border-components-button-color-outline-brand-default-stroke',
      'group',
      'h-[2.5rem]',
      'inline-flex',
      'items-center',
      'justify-center',
      'mr-2',
      'rounded-themes-spacing-radius-large-button',
      'w-[2.5rem]',
      'active:bg-components-button-color-outline-brand-pressed-bg',
      'active:border-components-button-color-outline-brand-pressed-stroke',
      'disabled:bg-transparent',
      'disabled:border-components-button-color-outline-brand-disabled-stroke',
      'disabled:pointer-events-none',
      'disabled:text-components-button-color-outline-white-disabled-text',
      'hover:bg-components-button-color-outline-brand-hover-bg',
      'hover:border-components-button-color-outline-brand-hover-stroke',
    ],
    container: ['relative', 'z-10'],
    contentCarousel: [
      'flex',
      'flex-1',
      'flex-col',
      'order-2',
      'lg:order-1',
      'max-lg:px-0',
      'max-lg:py-0',
    ],
    contentCarouselSingle: [
      'flex',
      'flex-1',
      'flex-col',
      'order-2',
      'lg:order-1',
      'max-lg:px-0',
      'max-lg:py-0',
      'sm:w-1/2',
      'md:w-1/3',
    ],
    contentCarouselTrack: [],
    controlContainerClasses: [
      'flex',
      'flex-row',
      'gap-6',
      'items-center',
      'justify-center',
      'md:justify-end',
      'md:px-themes-general-surface-spacing-large-padding-x',
    ],
    ctaWrapper: ['flex', 'justify-center', 'md:items-center'],
    descriptionText: [
      'font-bodySans-small',
      'leading-bodySans-small',
      'text-bodySans-small',
      'md:font-bodySans-medium',
      'md:leading-bodySans-medium',
      'md:text-bodySans-medium',
      'text-components-product-card-listing-color-copy',
    ],
    iconClasses: [
      'fill-components-button-color-outline-brand-default-text',
      'group-disabled:fill-components-button-color-outline-brand-disabled-text',
      'group-hover:fill-components-button-color-outline-brand-hover-text',
    ],
    iconNext: ['chevron_right'],
    iconPrev: ['chevron_left'],
    legalDisclaimerText: [
      'pt-spacing-spacing-5',
      '!text-components-product-card-listing-color-legal-text',
    ],
    playPauseFill: ['!hidden'], // We are not suppose to show Play button, in any secenario whether it is auto-play enabled or disabled, as per requirement.
    productCardListingCarousel: ['product-card-listing', 'w-full', '[&_li]:flex'],
    svgBg: [
      'absolute',
      'fill-white',
      'left-2/4',
      '-mt-[155px]', // MAGIC NUMBER: The desired offset from the center of the container
      'top-0',
      '-translate-x-2/4',
      '-translate-y-2/4',
      'w-[1168px]',
      'md:-mt-[745px]', // MAGIC NUMBER: The desired offset from the center of the container
      'md:w-[2488px]',
    ],
    svgDecorLower: ['absolute', 'bottom-0', 'hidden', 'left-0', 'w-[132px]', 'md:block'],
    svgDecorUpper: [
      'absolute',
      'hidden',
      'left-0',
      'm-auto',
      'mt-[110px]',
      'right-0',
      'top-0',
      'w-full',
      '[&>svg]:m-auto',
      '[&>svg]:w-full',
      'md:block',
    ],
    titleAndDescriptionWrapperInner: [
      'flex',
      'flex-col',
      'max-md:mb-themes-general-surface-spacing-small-body-margin-bottom',
    ],
    titleAndDescriptionWrapperOuter: [
      'flex',
      'flex-col',
      'justify-between',
      'text-center',
      'w-full',
      'md:flex-row',
      'md:text-start',
      'mb-themes-general-surface-spacing-small-body-margin-bottom',
      'md:mb-themes-general-surface-spacing-large-body-margin-bottom',
      'md:gap-x-themes-general-surface-spacing-large-body-margin-right',
    ],
    titleText: [
      'font-header-small-large',
      'leading-header-small-large',
      'text-header-small-large',
      'text-components-product-card-listing-color-title',
      'mb-themes-general-surface-spacing-small-title-margin-bottom',
      'md:font-header-large-large',
      'md:leading-header-large-large',
      'md:text-header-large-large',
      'md:mb-themes-general-surface-spacing-large-title-margin-bottom',
    ],
  },
  variants: {
    addMarginBottom: {
      undefined: {
        contentCarouselTrack: ['mb-spacing-margin-small-7', 'md:mb-spacing-margin-large-5'],
      },
      3: {
        contentCarouselTrack: ['md:mb-0', 'mb-spacing-margin-small-7'],
      },
      2: {
        contentCarouselTrack: ['sm:mb-0', 'mb-spacing-margin-small-7'],
      },
      1: {
        contentCarouselTrack: ['mb-0'],
      },
    },
    carouselMarginRight: {
      true: {
        contentCarousel: [
          'ltr:-mr-[calc(theme(spacing.themes-general-surface-spacing-small-padding-x))]',
          'ltr:md:-mr-[calc(theme(spacing.themes-general-surface-spacing-large-padding-x))]',
          'rtl:-ml-[calc(theme(spacing.themes-general-surface-spacing-small-padding-x))]',
          'rtl:md:-ml-[calc(theme(spacing.themes-general-surface-spacing-large-padding-x))]',
        ],
      },
      false: {
        contentCarousel: [],
      },
    },
    brand: {
      ...themeVariants,
      Off: createBrandVariants(
        ['bg-colors-brand-1-400'],
        'mt-[76px]',
        '-mt-[155px]',
        '-mt-[778px]'
      ),
      OffDefense: createBrandVariants(
        ['bg-components-product-card-listing-color-bg'],
        'mt-[76px]',
        '-mt-[155px]',
        '-mt-[778px]'
      ),
      Autan: createBrandVariants(
        ['bg-colors-brand-1-400'],
        'mt-[92px]',
        '-mt-[155px]',
        '-mt-[778px]'
      ),
      AutanDefense: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Glade: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Raid: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Duck: {
        svgBg: [
          '!w-full',
          '!h-full',
          '[&_svg]:h-full',
          '!-mt-0',
          '!left-0',
          '!translate-x-0',
          '!translate-y-0',
          '!fill-none',
        ],
      },
      Pyrel: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Baygon: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      MrMuscle: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Pledge: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Lysoform: {
        background: ['bg-components-product-card-listing-color-bg'],
        svgBg: [
          '!w-full',
          '!h-full',
          '[&_svg]:h-full',
          '!-mt-0',
          '!left-0',
          '!translate-x-0',
          '!translate-y-0',
          '!fill-none',
        ],
      },
      Windex: {
        svgBg: [
          '!w-full',
          '!h-full',
          '[&_svg]:h-full',
          '!-mt-0',
          '!left-0',
          '!translate-x-0',
          '!translate-y-0',
          '!fill-none',
        ],
        background: ['bg-components-product-card-listing-color-bg'],
      },
      Exposis: {
        background: ['bg-components-product-card-listing-color-bg'],
      },
      ScrubbingBubbles: {
        svgBg: [
          '!w-full',
          '!h-full',
          '[&_svg]:h-full',
          '!-mt-0',
          '!left-0',
          '!translate-x-0',
          '!translate-y-0',
          '!fill-none',
        ],
        background: ['bg-components-product-card-listing-color-bg'],
      },
    },
  },
});

function createBrandVariants(
  background: string[],
  svgDecorUpper: string,
  svgBgSmall: string,
  svgBgLarge: string
) {
  return {
    background,
    ctaWrapper: ['justify-center'],
    descriptionText: ['text-themes-general-card-color-dark-title'],
    productCardListingCarousel: ['m-auto', 'max-w-[1280px]'],
    titleAndDescriptionWrapperInner: [
      'md:mb-themes-general-surface-spacing-large-body-margin-bottom',
      'md:text-center',
    ],
    titleAndDescriptionWrapperOuter: ['md:flex-col'],
    titleText: ['text-themes-general-card-color-dark-title'],
    ...(svgDecorUpper && { svgDecorUpper }),
    svgBg: [...(svgBgSmall ? [`md:${svgBgLarge}`, svgBgSmall] : [])],
  };
}
